import { moveArray } from "@msuite/utils/array";

import type { RowData } from "@tanstack/react-table";
import React from "react";
import { Button } from "../../../button";
import { TextInput } from "../../../form";
import { useSearchForm } from "../../../hooks";
import { Icon, icon } from "../../../icons";
import { useTranslation } from "../../../localization";
import { Menu } from "../../../menu";
import { useDataTableContext } from "../context";
import { getHeaderMeta } from "../handlers";
import type { Header } from "../types";
import { ColumnResizer } from "./column-resizer";
import { SortingIndicator } from "./sorting-indicator";

/** Props Interface */
interface ColumnOptionsProps<T extends RowData> {
	header: Header<T, unknown>;
}

export function ColumnOptions<T extends RowData>({ header }: ColumnOptionsProps<T>) {
	/** Hooks */
	const { table } = useDataTableContext();
	const { t } = useTranslation();
	const methods = useSearchForm(header.getContext().column.id, 0);

	const metaOptionsRaw = getHeaderMeta(header).menuOptions;
	const metaOptions =
		typeof metaOptionsRaw === "function"
			? metaOptionsRaw({ table, header, columnId: header.column.id })
			: metaOptionsRaw;

	function handleOnReorderColumn(direction: "left" | "right") {
		const currentColumns = table.state.columnOrder;
		const currentIndex = currentColumns.indexOf(header.column.id);
		const newIndex = direction === "left" ? currentIndex - 1 : currentIndex + 1;
		const newColumns = moveArray(currentColumns, currentIndex, newIndex);
		table.setColumnOrder(newColumns);
	}

	/** Render */
	return (
		<React.Fragment>
			<div
				className="relative flex flex-row items-center ml-4 pr-0
			"
			>
				{header.column.getCanSort() && (
					<SortingIndicator
						columnId={header.column.id}
						onClick={() => {
							header.getContext().column.toggleSorting(undefined, true);
						}}
					/>
				)}
				<Menu
					type="menu"
					className="relative"
					items={() => [
						{
							label: "",
							icon: undefined,
							render: () => {
								return (
									<TextInput
										control={methods.control}
										isDisabled={!header.column.getCanFilter()}
										type="search"
										name="searchValue"
										label={undefined}
										placeholder={t("placeholders.search")}
										isRequired={false}
										outputMiddleware={(value) => {
											header.column.setFilterValue(value);
											return value;
										}}
									/>
								);
							},
						},
						...(metaOptions ? metaOptions : []),
						"divider",
						{
							label: t("tables.sort_ascending"),
							onClick: () => {
								header.column.toggleSorting(false, true);
							},
							isActive: header.column.getIsSorted() === "asc",
							icon: icon("ArrowUp"),
							isDisabled: !header.column.getCanSort(),
						},
						{
							label: t("tables.sort_descending"),
							onClick: () => {
								header.column.toggleSorting(true, true);
							},
							isActive: header.column.getIsSorted() === "desc",
							icon: icon("ArrowDown"),
							isDisabled: !header.column.getCanSort(),
						},
						{
							label: t("tables.reset_sorting"),
							onClick: () => {
								header.getContext().column.clearSorting();
							},
							icon: icon("ArrowDownUp"),
							isDisabled: !header.column.getCanSort(),
						},
						"divider",
						{
							label:
								header.column.getIsPinned() === "start" ? t("tables.unpin") : t("tables.pin_left"),
							onClick: () => {
								const isPinned = header.column.getIsPinned();
								if (isPinned === "start") {
									header.column.pin(false);
									return;
								}
								header.column.pin("start");
							},
							icon: icon("ArrowLeftFromLine"),
							isDisabled: !header.column.getCanPin(),
						},
						{
							label:
								header.column.getIsPinned() === "end" ? t("tables.unpin") : t("tables.pin_right"),
							onClick: () => {
								const isPinned = header.column.getIsPinned();
								if (isPinned === "end") {
									header.column.pin(false);
									return;
								}
								header.column.pin("end");
							},
							icon: icon("ArrowRightFromLine"),
							isDisabled: !header.column.getCanPin(),
						},
						{
							label: t("tables.hide_column"),
							onClick: () => {
								header.column.toggleVisibility();
							},
							icon: icon("EyeOff"),
							isDisabled: !header.column.getCanHide(),
						},
						"divider",
						{
							label: t("tables.move_column_left"),
							onClick: () => {
								handleOnReorderColumn("left");
							},
							icon: icon("ArrowLeftFromLine"),
							isDisabled: !!header.column.getIsPinned(),
						},
						{
							label: t("tables.move_column_right"),
							onClick: () => {
								handleOnReorderColumn("right");
							},
							icon: icon("ArrowRightFromLine"),
							isDisabled: !!header.column.getIsPinned(),
						},
					]}
				>
					<div>
						<Button
							size="xxs"
							variant="ghost"
							icon={<Icon name="EllipsisVertical" />}
						/>
					</div>
				</Menu>
			</div>
			<ColumnResizer header={header} />
		</React.Fragment>
	);
}
